import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { format, formatISO9075, isValid, parse, parseISO } from 'date-fns';

import { mifidApi } from '@/api/mifid';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { bulkTradeApi } from '@/store/bulkTrade/api';
import type { TradeCaptureFieldError, TradeCaptureLeg } from '@/store/bulkTrade/api/checkBulkTradeByTradeCapture.ts';
import type {
  BulkCashLeg,
  BulkLeg,
  BulkLegId,
  BulkPricedCashNettedLeg,
  BulkPricedCashSplitLeg,
  BulkSwapLeg,
  BulkTradeById,
  BulkTradePriced,
} from '@/store/bulkTrade/state.ts';
import { isCashLeg } from '@/store/bulkTrade/utilities/guards.ts';
import type { CurrencyPair } from '@/types/currency.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { roundToFixed } from '@/utils/number.ts';
import type { ReceivedBulkQuoteReply } from '@/utils/rfs/type.ts';

export const bulkTradeSlice = createSlice({
  name: 'bulkTrade',
  reducerPath: 'bulkTrade',

  initialState: {} as BulkTradeById,

  reducers: {
    currencyPairChanged: (
      state,
      action: PayloadAction<{ tabId: string; bulkTradeId: string; currencyPair: CurrencyPair }>,
    ) => {
      const currentBulkTrade = state[action.payload.tabId];
      const tradeCaptureVersion = currentBulkTrade?.tradeCaptureVersion ?? -1;

      const [currency1, currency2] = action.payload.currencyPair.split('/') as [string, string];

      // Preserve existing legs when the currency pair is changed on an existing trade.
      // Only initialize with a single Spot leg when creating a trade for the first time.
      const existingBuildingTrade =
        isDefined(currentBulkTrade) && currentBulkTrade.step === 'building' ? currentBulkTrade : undefined;

      const initialLeg = {
        0: {
          id: '0',
          type: 'Spot',

          way: 'Buy',

          maturityDate: undefined,
          maturityDateTenor: 'SP',

          notional: undefined,
        } satisfies BulkLeg,
      };

      return {
        ...state,

        [action.payload.tabId]: {
          id: action.payload.bulkTradeId,
          step: 'building',
          currencyPair: action.payload.currencyPair,
          currency1,
          currency2,

          isPriceObsolete: false,
          isReadyToPrice: false,
          isProductObsolete: false,

          negotiatedCurrencyIndex: existingBuildingTrade?.negotiatedCurrencyIndex ?? 1,

          nextLegId: existingBuildingTrade?.nextLegId ?? 1,

          legs: existingBuildingTrade?.legs ?? initialLeg,

          allSelectedLegs: [],
          allExcludedLegs: [],

          tradeCaptureVersion,
        },
      };
    },

    tieringChanged: (state, action: PayloadAction<{ tabId: string; tiering: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,
          tiering: action.payload.tiering,
        },
      };
    },

    toggleLegSelection: (state, action: PayloadAction<{ tabId: string; legId: BulkLegId }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      const isSelected = existingTrade.allSelectedLegs.includes(action.payload.legId);

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,
          allSelectedLegs: isSelected
            ? existingTrade.allSelectedLegs.filter((id) => id !== action.payload.legId)
            : [...existingTrade.allSelectedLegs, action.payload.legId],
        },
      };
    },

    toggleAllLegsSelection: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      const allLegIds = Object.keys(existingTrade.legs);
      const areAllSelected = existingTrade.allSelectedLegs.length === allLegIds.length;

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,
          allSelectedLegs: areAllSelected ? [] : allLegIds,
        },
      };
    },

    clearSelectedLegs: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,

          allSelectedLegs: [],
        },
      };
    },

    changeLegProduct: (
      state,
      action: PayloadAction<{ tabId: string; legId: BulkLegId; productName: BulkLeg['type'] }>,
    ) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      const legToUpdate = existingTrade.legs[action.payload.legId];

      if (isNotDefined(legToUpdate)) {
        return state;
      }

      return {
        ...state,

        [action.payload.tabId]: {
          ...existingTrade,

          legs: {
            ...existingTrade.legs,

            [action.payload.legId]:
              action.payload.productName === 'Swap'
                ? ({
                    id: action.payload.legId,
                    type: action.payload.productName,
                    isUneven: false,
                    way: 'BuyThenSell',

                    nearNotional: undefined,
                    nearExpiryDate: undefined,
                    nearExpiryDateTenor: undefined,

                    farNotional: undefined,
                    farExpiryDate: undefined,
                    farExpiryDateTenor: undefined,
                  } satisfies BulkSwapLeg)
                : ({
                    id: action.payload.legId,
                    type: action.payload.productName,
                    way: 'Buy',

                    notional: undefined,
                    maturityDate: undefined,
                    maturityDateTenor: undefined,
                  } satisfies BulkCashLeg),
          },
        },
      };
    },

    bulkTradeExecutionRejected: (
      state,
      action: PayloadAction<{
        tabId: string;
        errorCode: string;
        multipassErrorCode?: string;
        reason?: string;
        sessionId?: string;
      }>,
    ) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'executed') {
        return state;
      }

      state[action.payload.tabId] = {
        ...existingTrade,
        status: 'rejected',
        errorCode: action.payload.errorCode,
        reason: action.payload.reason,
        multipassErrorCode: action.payload.multipassErrorCode,
        sessionId: action.payload.sessionId,
      };
    },

    excludeSelectedLegs: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,
          allSelectedLegs: [],
          allExcludedLegs: [...new Set([...existingTrade.allExcludedLegs, ...existingTrade.allSelectedLegs])],
        },
      };
    },

    includeSelectedLegs: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade)) {
        return state;
      }

      return {
        ...state,
        [action.payload.tabId]: {
          ...existingTrade,
          allSelectedLegs: [],
          allExcludedLegs: existingTrade.allExcludedLegs.filter((id) => !existingTrade.allSelectedLegs.includes(id)),
        },
      };
    },

    quoteReceived: (
      state,
      action: PayloadAction<{
        tabId: string;
        workerTimestamp: Timestamp;
        rfsId: string;
        quote: ReceivedBulkQuoteReply;
      }>,
    ) => {
      const existingTrade = state[action.payload.tabId];

      if (
        isNotDefined(existingTrade) ||
        (existingTrade.step !== 'building' && existingTrade.step !== 'priced') ||
        isNotDefined(existingTrade.currencyPair) ||
        isNotDefined(existingTrade.tiering)
      ) {
        return state;
      }

      const allNettedLegs: BulkPricedCashNettedLeg[] = action.payload.quote.NettedView.map((nettedLeg, legId) => {
        const existingNettedLeg = existingTrade.step === 'priced' ? existingTrade.nettedLegs[legId] : undefined;

        const spotCommercialMarginPPS =
          existingNettedLeg?.spotCommercialMarginPPS ??
          (nettedLeg.CustomerWay === 'Buy'
            ? Number(nettedLeg.DefaultMargin?.Ask ?? 0)
            : Number(nettedLeg.DefaultMargin?.Bid ?? 0));

        return {
          id: String(legId),
          spotCommercialMarginPPS,

          quoteLeg: nettedLeg,
        };
      });

      const allSplitLegs: BulkPricedCashSplitLeg[] = action.payload.quote.Legs.map((splitLeg, legId) => {
        return {
          id: String(legId),

          quoteLeg: splitLeg,
        };
      });

      const newBulkTrade: BulkTradePriced = {
        id: existingTrade.id,
        step: 'priced',

        currencyPair: existingTrade.currencyPair,
        currency1: existingTrade.currency1,
        currency2: existingTrade.currency2,

        tiering: existingTrade.tiering,

        negotiatedCurrencyIndex: existingTrade.negotiatedCurrencyIndex,

        nextLegId: existingTrade.nextLegId,
        legs: existingTrade.legs,

        allSelectedLegs: existingTrade.allSelectedLegs,
        allExcludedLegs: existingTrade.allExcludedLegs,

        isPriceObsolete: existingTrade.isPriceObsolete,
        isReadyToPrice: existingTrade.isReadyToPrice,
        isProductObsolete: existingTrade.isProductObsolete,

        tradeCaptureVersion: existingTrade.tradeCaptureVersion,

        nettedLegs: allNettedLegs,
        splitLegs: allSplitLegs,

        rfsRequestedAt:
          (existingTrade.step === 'building' ? existingTrade.rfsRequest?.requestedAt : existingTrade.rfsRequestedAt) ??
          Date.now(), // rfsRequestedAt is set during the step
        quote: action.payload.quote,
        quoteBackTimestamp: action.payload.quote.Timestamp,
        quoteFrontTimestamp: action.payload.workerTimestamp,

        rfsWindow: existingTrade.step === 'building' ? action.payload.quote.RfsWindow : existingTrade.rfsWindow,
        rfsRemainingTime: action.payload.quote.RfsWindow,

        negociations: existingTrade.step === 'building' ? [] : existingTrade.negociations,
      };

      return {
        ...state,

        [action.payload.tabId]: newBulkTrade,
      };
    },

    spotCommercialMarginPPSChanged: (
      state,
      action: PayloadAction<{ tabId: string; legId: BulkLegId; value: number }>,
    ) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'priced') {
        return;
      }

      const updatedLeg = existingTrade.nettedLegs.find((leg) => leg.id === action.payload.legId);

      if (isNotDefined(updatedLeg) || isNotDefined(updatedLeg.spotCommercialMarginPPS)) {
        return;
      }

      updatedLeg.spotCommercialMarginPPS = roundToFixed(action.payload.value, 1);
    },

    spotCommercialMarginPPSIncremented: (state, action: PayloadAction<{ tabId: string; legId: BulkLegId }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'priced') {
        return;
      }

      const updatedLeg = existingTrade.nettedLegs.find((leg) => leg.id === action.payload.legId);

      if (isNotDefined(updatedLeg) || isNotDefined(updatedLeg.spotCommercialMarginPPS)) {
        return;
      }

      updatedLeg.spotCommercialMarginPPS = roundToFixed(updatedLeg.spotCommercialMarginPPS + 0.1, 1);
    },

    spotCommercialMarginPPSDecremented: (state, action: PayloadAction<{ tabId: string; legId: BulkLegId }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'priced') {
        return;
      }

      const updatedLeg = existingTrade.nettedLegs.find((leg) => leg.id === action.payload.legId);

      if (isNotDefined(updatedLeg) || isNotDefined(updatedLeg.spotCommercialMarginPPS)) {
        return;
      }

      updatedLeg.spotCommercialMarginPPS = roundToFixed(updatedLeg.spotCommercialMarginPPS - 0.1, 1);
    },

    rfsRequestFailed: (
      state,
      action: PayloadAction<{ tabId: string; code: string; message: string; rfsId: string }>,
    ) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'building') {
        return;
      }

      existingTrade.rfsRequest = {
        status: 'error',
        requestedAt: existingTrade?.rfsRequest?.requestedAt ?? Date.now(),
        rfsId: existingTrade?.rfsRequest?.rfsId ?? action.payload.rfsId,
        code: action.payload.code,
        message: action.payload.message,
      };
    },

    executionSuccess: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'executed' || existingTrade.status !== 'pending') {
        return;
      }

      state[action.payload.tabId] = {
        ...existingTrade,

        status: 'done',
      };
    },

    executionConfirmed: (state, action: PayloadAction<{ tabId: string; tradeIds: string[] }>) => {
      const existingTrade = state[action.payload.tabId];

      if (
        isNotDefined(existingTrade) ||
        existingTrade.step !== 'executed' ||
        (existingTrade.status !== 'pending' && existingTrade.status !== 'done')
      ) {
        return;
      }

      state[action.payload.tabId] = {
        ...existingTrade,

        status: 'done',
        tradeIds: action.payload.tradeIds,
      };
    },

    canceled: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (
        isNotDefined(existingTrade) ||
        existingTrade.step === 'executed' ||
        (existingTrade.step === 'building' && isNotDefined(existingTrade.rfsRequest?.rfsId))
      ) {
        return;
      }

      state[action.payload.tabId] = {
        id: existingTrade.id,
        step: 'building',
        currencyPair: existingTrade.currencyPair,
        currency1: existingTrade.currency1,
        currency2: existingTrade.currency2,

        tiering: existingTrade.tiering,

        isPriceObsolete: existingTrade.isPriceObsolete,
        isReadyToPrice: existingTrade.isReadyToPrice,
        isProductObsolete: existingTrade.isProductObsolete,

        negotiatedCurrencyIndex: existingTrade.negotiatedCurrencyIndex,

        nextLegId: existingTrade.nextLegId,

        legs: existingTrade.legs,

        allSelectedLegs: existingTrade.allSelectedLegs,
        allExcludedLegs: existingTrade.allExcludedLegs,

        tradeCaptureVersion: existingTrade.tradeCaptureVersion,
      };
    },

    reset: (state, action: PayloadAction<{ tabId: string }>) => {
      delete state[action.payload.tabId];
    },

    done: (state, action: PayloadAction<{ tabId: string }>) => {
      const existingTrade = state[action.payload.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'executed') {
        return;
      }

      state[action.payload.tabId] = {
        id: existingTrade.id,
        step: 'building',

        currencyPair: existingTrade.currencyPair,
        currency1: existingTrade.currency1,
        currency2: existingTrade.currency2,

        tiering: existingTrade.tiering,

        negotiatedCurrencyIndex: existingTrade.negotiatedCurrencyIndex,

        nextLegId: existingTrade.nextLegId,
        legs: existingTrade.legs,

        allSelectedLegs: existingTrade.allSelectedLegs,
        allExcludedLegs: existingTrade.allExcludedLegs,

        isPriceObsolete: existingTrade.isPriceObsolete,
        isReadyToPrice: existingTrade.isReadyToPrice,
        isProductObsolete: existingTrade.isProductObsolete,

        tradeCaptureVersion: existingTrade.tradeCaptureVersion,

        rfsRequest: undefined,
      };
    },
  },

  extraReducers: (builder) => {
    // ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
    // ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
    //    ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
    //    ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
    //    ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
    //    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

    builder.addMatcher(bulkTradeApi.endpoints.checkBulkTradeByTradeCapture.matchFulfilled, (state, action) => {
      const existingTrade = state[action.meta.arg.originalArgs.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'building') {
        return;
      }

      const currencyPair = action.payload.changedFields.currencyPair ?? existingTrade.currencyPair;
      const [currency1, currency2] = currencyPair.split('/') as [string, string];

      state[action.meta.arg.originalArgs.tabId] = {
        id: existingTrade.id,
        step: 'building',
        currencyPair,
        currency1,
        currency2,
        tiering: existingTrade.tiering,
        negotiatedCurrencyIndex:
          action.payload.changedFields.negotiatedCurrency ?? existingTrade.negotiatedCurrencyIndex,
        nextLegId: Math.max(
          existingTrade.nextLegId,
          ...Object.keys(action.payload.changedFields.legs ?? {}).map((legId) => Number.parseInt(legId, 10) + 1),
        ),

        legs: Object.entries(action.payload.changedFields.legs).reduce(
          (acc, [legId, leg]) => {
            const legErrors = action.payload.errors?.legs?.[legId];
            acc[legId] =
              leg.productName === 'FxSpot' || leg.productName === 'FxFwd'
                ? updateBulkTradeCashLeg(legId, leg, legErrors, existingTrade.legs[legId])
                : updateBulkTradeSwapLeg(
                    legId,
                    leg,
                    legErrors,
                    existingTrade.legs[legId],
                    existingTrade.negotiatedCurrencyIndex,
                  );

            return acc;
          },
          {} as Record<BulkLegId, BulkLeg>,
        ),

        allSelectedLegs: existingTrade.allSelectedLegs.filter((legId) => legId in action.payload.changedFields.legs),
        allExcludedLegs: existingTrade.allExcludedLegs.filter((legId) => legId in action.payload.changedFields.legs),

        isPriceObsolete: action.payload.changedFields.isPriceObsolete,
        isProductObsolete: action.payload.changedFields.isProductObsolete,
        isReadyToPrice: action.payload.changedFields.isReadyToPrice,
        tradeCaptureVersion: action.payload.idVersion,
      };
    });

    // ██████╗ ███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝
    // ██████╔╝█████╗  ███████╗
    // ██╔══██╗██╔══╝  ╚════██║
    // ██║  ██║██║     ███████║
    // ╚═╝  ╚═╝╚═╝     ╚══════╝

    builder.addMatcher(bulkTradeApi.endpoints.requestBulkTradeRfs.matchPending, (state, action) => {
      const existingTrade = state[action.meta.arg.originalArgs.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'building') {
        return;
      }

      existingTrade.rfsRequest = {
        status: 'pending',
        requestedAt: action.meta.startedTimeStamp,
        rfsId: action.meta.arg.originalArgs.rfsId,
      };
    });

    // ██████╗ ███████╗ ██████╗ ██████╗ ██████╗ ██████╗     ██████╗ ██████╗ ██╗ ██████╗███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝██╔═══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██║██╔════╝██╔════╝██╔════╝
    // ██████╔╝█████╗  ██║     ██║   ██║██████╔╝██║  ██║    ██████╔╝██████╔╝██║██║     █████╗  ███████╗
    // ██╔══██╗██╔══╝  ██║     ██║   ██║██╔══██╗██║  ██║    ██╔═══╝ ██╔══██╗██║██║     ██╔══╝  ╚════██║
    // ██║  ██║███████╗╚██████╗╚██████╔╝██║  ██║██████╔╝    ██║     ██║  ██║██║╚██████╗███████╗███████║
    // ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝╚══════╝

    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const existingTrade = state[action.meta.arg.originalArgs.tileId]; // use tileId for tabId

      if (isNotDefined(existingTrade) || existingTrade.step !== 'priced') {
        return;
      }

      existingTrade.negociations.push(formatISO9075(action.meta.arg.originalArgs.date));
    });

    // ███████╗██╗  ██╗███████╗ ██████╗██╗   ██╗████████╗██╗ ██████╗ ███╗   ██╗
    // ██╔════╝╚██╗██╔╝██╔════╝██╔════╝██║   ██║╚══██╔══╝██║██╔═══██╗████╗  ██║
    // █████╗   ╚███╔╝ █████╗  ██║     ██║   ██║   ██║   ██║██║   ██║██╔██╗ ██║
    // ██╔══╝   ██╔██╗ ██╔══╝  ██║     ██║   ██║   ██║   ██║██║   ██║██║╚██╗██║
    // ███████╗██╔╝ ██╗███████╗╚██████╗╚██████╔╝   ██║   ██║╚██████╔╝██║ ╚████║
    // ╚══════╝╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═════╝    ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝

    builder.addMatcher(bulkTradeApi.endpoints.executeBulkTradeRfs.matchPending, (state, action) => {
      const existingTrade = state[action.meta.arg.originalArgs.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'priced') {
        return;
      }

      state[action.meta.arg.originalArgs.tabId] = {
        id: existingTrade.id,
        step: 'executed',
        status: 'pending',

        currencyPair: existingTrade.currencyPair,
        currency1: existingTrade.currency1,
        currency2: existingTrade.currency2,

        negotiatedCurrencyIndex: existingTrade.negotiatedCurrencyIndex,

        tiering: existingTrade.tiering,

        isPriceObsolete: existingTrade.isPriceObsolete,
        isReadyToPrice: existingTrade.isReadyToPrice,
        isProductObsolete: existingTrade.isProductObsolete,

        tradeCaptureVersion: existingTrade.tradeCaptureVersion,

        nextLegId: existingTrade.nextLegId,
        legs: existingTrade.legs,

        allSelectedLegs: existingTrade.allSelectedLegs,
        allExcludedLegs: existingTrade.allExcludedLegs,

        nettedLegs: existingTrade.nettedLegs,
        splitLegs: existingTrade.splitLegs,

        rfsRequestedAt: existingTrade.rfsRequestedAt,
        quote: existingTrade.quote,

        negociations: existingTrade.negociations,
        quoteBackTimestamp: existingTrade.quoteBackTimestamp,
        quoteFrontTimestamp: existingTrade.quoteFrontTimestamp,
      };
    });

    builder.addMatcher(bulkTradeApi.endpoints.executeBulkTradeRfs.matchRejected, (state, action) => {
      const existingTrade = state[action.meta.arg.originalArgs.tabId];

      if (isNotDefined(existingTrade) || existingTrade.step !== 'executed') {
        return;
      }

      const payload = action.payload;
      if (isNotDefined(payload)) {
        state[action.meta.arg.originalArgs.tabId] = {
          ...existingTrade,
          status: 'rejected',
          errorCode: 'BULK_REQUEST_INTERNAL_ERROR',
        };
      } else {
        const error = mapHttpStatusCode(payload.status);
        if (error === 'TIMEOUT') {
          logger.logWarning(
            '{instrument} {requestType} quote execution has being manually rejected by MultipassBridge because timeout',
            'RFS',
            'Bulk',
          );
        }

        state[action.meta.arg.originalArgs.tabId] = {
          ...existingTrade,
          status: 'rejected',
          errorCode: error,
          reason: isDefined(payload.data) && typeof payload.data === 'string' ? payload.data : undefined,
        };
      }
    });
  },
});

function mapHttpStatusCode(
  statusCode: number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR',
): string {
  switch (statusCode) {
    case 404:
      return 'RFS_NOT_FOUND';
    case 403:
      return 'NOT_AUTHORIZED';
    case 408:
      return 'TIMEOUT';
    default:
      return 'BULK_REQUEST_INTERNAL_ERROR';
  }
}

function updateBulkTradeCashLeg(
  legId: string,
  changedLeg: TradeCaptureLeg,
  legError: Record<string, TradeCaptureFieldError[]> | undefined,
  currentLeg: BulkLeg | undefined,
): BulkLeg {
  // null ?? undefined = undefined
  // => if amount1 is null, keep this value
  const newNotional = changedLeg.amount1 === null ? null : (changedLeg.amount1 ?? changedLeg.amount2);

  return {
    id: legId,
    type: changedLeg.productName === 'FxSpot' ? 'Spot' : 'Forward',

    way:
      changedLeg.way ??
      (currentLeg?.type === 'Swap' ? (currentLeg?.way === 'BuyThenSell' ? 'Buy' : 'Sell') : currentLeg?.way) ??
      'Buy',

    maturityDate:
      getPreviousDate(legError?.['maturityDate']?.[0]?.previousValue) ??
      getValueFromTradeCapture(changedLeg.maturityDate, isCashLeg(currentLeg) ? currentLeg?.maturityDate : undefined),

    maturityDateTenor: getValueFromTradeCapture(
      changedLeg.maturityDateTenor,
      isCashLeg(currentLeg) ? currentLeg?.maturityDateTenor : currentLeg?.nearExpiryDateTenor,
    ),

    maturityDateErrors:
      // take returned error
      legError?.['maturityDate'] ??
      // or keep existing error if maturity date is not changed and there is already an error
      (isDefined(changedLeg.maturityDate)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.maturityDateErrors
          : currentLeg?.nearExpiryDateErrors),

    notional:
      toNumberOrUndefined(legError?.['amount1']?.[0]?.previousValue) ??
      toNumberOrUndefined(legError?.['amount2']?.[0]?.previousValue) ??
      getValueFromTradeCapture(
        newNotional,
        currentLeg?.type === 'Swap' ? currentLeg?.nearNotional : currentLeg?.notional,
      ),

    notionalErrors:
      // take returned error
      legError?.['amount1'] ??
      legError?.['amount2'] ??
      // or keep existing error if notional is not changed and there is already an error
      (isDefined(newNotional)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.notionalErrors
          : currentLeg?.nearNotionalErrors),
  };
}

function updateBulkTradeSwapLeg(
  legId: string,
  changedLeg: TradeCaptureLeg,
  legError: Record<string, TradeCaptureFieldError[]> | undefined,
  currentLeg: BulkLeg | undefined,
  currentNegotiatedCurrencyIndex: CurrencyIndex,
): BulkLeg {
  const negotiatedCurrency = changedLeg.negotiatedCurrency ?? currentNegotiatedCurrencyIndex; // in prod, trade capture seems to not always return the negotiatedCurrency

  // null ?? undefined = undefined
  // => if amount1 is null, keep this value
  const newNearNotional = negotiatedCurrency === 1 ? changedLeg.nearAmount1 : changedLeg.nearAmount2;
  const newFarNotional = negotiatedCurrency === 1 ? changedLeg.farAmount1 : changedLeg.farAmount2;

  return {
    id: legId,
    type: 'Swap',

    // TODO: update the calculation
    way: isDefined(changedLeg.way)
      ? changedLeg.way === 'Buy'
        ? 'BuyThenSell'
        : 'SellThenBuy'
      : currentLeg === undefined || currentLeg.way === 'Buy' || currentLeg.way === 'BuyThenSell'
        ? 'BuyThenSell'
        : 'SellThenBuy',

    isUneven: changedLeg.isUneven ?? (currentLeg?.type === 'Swap' ? currentLeg?.isUneven : false) ?? false,

    // near data
    nearNotional:
      toNumberOrUndefined(legError?.['nearAmount1']?.[0]?.previousValue) ??
      toNumberOrUndefined(legError?.['nearAmount2']?.[0]?.previousValue) ??
      getValueFromTradeCapture(
        newNearNotional,
        currentLeg?.type === 'Swap' ? currentLeg?.nearNotional : currentLeg?.notional,
      ),

    nearNotionalErrors:
      // take returned error
      legError?.['nearAmount1'] ??
      legError?.['nearAmount2'] ??
      // or keep existing error if notional is not changed and there is already an error
      (isDefined(newNearNotional)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.notionalErrors
          : currentLeg?.nearNotionalErrors),

    nearExpiryDate:
      getPreviousDate(legError?.['nearPaymentDate']?.[0]?.previousValue) ??
      getValueFromTradeCapture(
        changedLeg.nearPaymentDate,
        currentLeg?.type === 'Swap' ? currentLeg?.nearExpiryDate : currentLeg?.maturityDate,
      ),

    nearExpiryDateTenor: getValueFromTradeCapture(
      changedLeg.nearPaymentDateTenor,
      currentLeg?.type === 'Swap' ? currentLeg?.nearExpiryDateTenor : currentLeg?.maturityDateTenor,
    ),

    nearExpiryDateErrors:
      // take returned error
      legError?.['nearPaymentDate'] ??
      // or keep existing error if maturity date is not changed and there is already an error
      (isDefined(changedLeg.nearPaymentDate)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.maturityDateErrors
          : currentLeg?.nearExpiryDateErrors),

    // far data
    farNotional:
      toNumberOrUndefined(legError?.['farAmount1']?.[0]?.previousValue) ??
      toNumberOrUndefined(legError?.['farAmount2']?.[0]?.previousValue) ??
      getValueFromTradeCapture(
        newFarNotional,
        currentLeg?.type === 'Swap' ? currentLeg?.farNotional : currentLeg?.notional,
      ),

    farNotionalErrors:
      // take returned error
      legError?.['farAmount1'] ??
      legError?.['farAmount2'] ??
      // or keep existing error if notional is not changed and there is already an error
      (isDefined(newFarNotional)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.notionalErrors
          : currentLeg?.farNotionalErrors),

    farExpiryDate:
      getPreviousDate(legError?.['farPaymentDate']?.[0]?.previousValue) ??
      getValueFromTradeCapture(
        changedLeg.farPaymentDate,
        currentLeg?.type === 'Swap' ? currentLeg?.farExpiryDate : undefined,
      ),

    farExpiryDateTenor:
      changedLeg.farPaymentDateTenor ?? (currentLeg?.type === 'Swap' ? currentLeg?.farExpiryDateTenor : undefined),

    farExpiryDateErrors:
      // take returned error
      legError?.['farPaymentDate'] ??
      // or keep existing error if maturity date is not changed and there is already an error
      (isDefined(changedLeg.farPaymentDate)
        ? undefined
        : isCashLeg(currentLeg)
          ? currentLeg?.maturityDateErrors
          : currentLeg?.farExpiryDateErrors),
  };
}

function toNumberOrUndefined(value: string | undefined): number | undefined {
  if (isNotDefined(value)) {
    return undefined;
  }

  const parsed = Number.parseFloat(value);

  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Normally, a date is in the ISO format: YYYY-MM-dd.
 * But in the TradeCapture fake, it can be also in the format dd MMM YYYY, so we need to parse it before to manipulate it.
 */
function getPreviousDate(date: string | undefined) {
  if (isNotDefined(date)) {
    return undefined;
  }

  if (isValid(parseISO(date))) {
    return date;
  }

  const convertedDate = parse(date, 'dd-MMM-yy', new Date());

  return format(convertedDate, 'yyyy-MM-dd');
}

// null ?? undefined = undefined
// => if value is null, keep this value
// => if value is undefiend, trade capture doesn't return it, so get the previous value
function getValueFromTradeCapture<TValue extends string | number>(
  value: TValue | null | undefined,
  defaultValue: TValue | undefined,
) {
  return value === null ? undefined : (value ?? defaultValue);
}
